import React, { useContext } from 'react';

import { Tooltip } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { format } from 'date-fns';
import { NavLink } from 'react-router-dom';

import { FadeInComponent } from '@/components/FadeInComponent';
import { Account } from '@/core/api/collections/currentAccount';
import { AuthContext } from '@/core/auth/AuthContext';
import { StickyHeader } from '@/core/users/StickyHeader';
import { UserDetails } from '@/core/users/UserPermissions/UserDetails/UserDetails';
import UserEditConfirmModal from '@/core/users/UserPermissions/UserEditConfirmModal';
import { UserEditContext } from '@/core/users/UserPermissions/UserEditContext';
import { UserEditField } from '@/core/users/UserPermissions/userEditValidators';
import { UserPermissionsContentWidth } from '@/core/users/UserPermissions/UserPermissionsContentWidth';
import { UserPermissionsEditPermissionsContainer } from '@/core/users/UserPermissions/UserPermissionsEditPermissionsContainer';
import { UserPermissionsEditTeamsContainer } from '@/core/users/UserPermissions/UserPermissionsEditTeamsContainer';
import { UserPermissionsTabs } from '@/core/users/UserPermissions/UserPermissionsTabs';
import { UsersNavigationBar } from '@/core/users/UsersNavigationBar';
import { ReactComponent as ArrowLeft } from '@/icons/arrow_left.svg';
import { ReactComponent as SendMailIcon } from '@/icons/send_mail.svg';
import { ReactComponent as WarningIcon } from '@/icons/warning.svg';
import routes from '@/routes';

export function UserPermissions () {
  const { currentAccount } = useContext(AuthContext);
  const { billingRate, confirmModalOpen, isDirty, loading, handlePersist, setConfirmModalOpen, user } = useContext(UserEditContext);
  const invited = user?.permissions.core?.state === 'invited' || user?.permissions.uvk?.state === 'invited';

  return (
    <>
      <div className="z-10 border-b border-gray-300">
        <UsersNavigationBar>
          <NavLink to={routes.users.self} className="flex items-center p-6 -ml-6">
            <ArrowLeft className="w-5 h-4 mr-3" />
              User Management
          </NavLink>
        </UsersNavigationBar>
      </div>
      <main className="min-h-screen pb-20 text-gray-800">
        <StickyHeader renderNotification={(
          <FadeInComponent key="billing-header" className="pr-6 col-start-2 col-span-full">
            <BillingHeader account={currentAccount} rate={billingRate} />
          </FadeInComponent>
        )}>
          {loading && (
            <LoadingSkeleton />
          )}
          {user && (
            <UserDetails
              user={user}
              actionButton={(!invited || isDirty) && <SubmitButton />}
              resendInvitationButton={invited && <ResendInvitationButton />}
            />
          )}
          <div className={`col-start-2 col-span-full ${loading ? 'opacity-0' : ''}`}>
            <UserPermissionsTabs />
          </div>
        </StickyHeader>
        {user && (
          <UserPermissionsContentWidth>
            <UserPermissionsEditPermissionsContainer />
            <UserPermissionsEditTeamsContainer />
          </UserPermissionsContentWidth>
        )}
      </main>
      {currentAccount && (
        <UserEditConfirmModal
          account={currentAccount}
          onClose={() => setConfirmModalOpen(false)}
          onSubmit={handlePersist}
          open={confirmModalOpen}
          rate={billingRate}
        />
      )}
    </>
  );
}

function SubmitButton () {
  const { billingRate, errors, handlePersist, isDirty, isPersisting, setConfirmModalOpen } = useContext(UserEditContext);

  const errorKeyToDisplay = Object.keys(errors).find(key => !!errors[key as UserEditField]);
  let disabledButtonTooltip = '';
  if (errorKeyToDisplay) {
    disabledButtonTooltip = errors[errorKeyToDisplay as UserEditField]!;
  } else if (!isDirty) {
    disabledButtonTooltip = 'No changes have been made';
  }
  let isSubmitDisabled = isPersisting || !!disabledButtonTooltip;

  const onClick = () => {
    if (billingRate) {
      setConfirmModalOpen(true);
    } else {
      handlePersist();
    }
  };

  return (
    <Tooltip title={disabledButtonTooltip}>
      <button
        className={`${isSubmitDisabled ? 'bg-gray-200 text-gray-600' : 'text-blue-100 bg-blue-900 font-medium'} px-6 py-4 rounded evt-save-permissions-changes`}
        onClick={isSubmitDisabled ? undefined : onClick}
        style={{ width: 150 }}
      >
        {isPersisting ? 'Saving...' : 'Save Changes'}
      </button>
    </Tooltip>
  );
}

function ResendInvitationButton () {
  const { currentUser } = useContext(AuthContext);
  const { isDirty, resendInvitation, resendingInvitation, user } = useContext(UserEditContext);
  const canResendInvitations = currentUser!.coreOwner || currentUser!.uvkAdmin;
  let disabledButtonTooltip = '';
  if (isDirty) {
    disabledButtonTooltip = 'Save changes before resending invitation';
  }
  if (!canResendInvitations) {
    disabledButtonTooltip = 'You do not have permission to resend invitations. Please contact your account owner for assistance.';
  }
  const isSubmitDisabled = isDirty || resendingInvitation || !canResendInvitations;
  const defaultClasses = 'flex items-center px-6 py-4 rounded font-medium evt-invite-users whitespace-nowrap mr-4';
  const enabledClasses = 'text-gray-100 bg-blue-900';
  const disabledClasses = 'text-gray-600 bg-gray-200';

  return (
    <Tooltip title={disabledButtonTooltip}>
      <button
        className={`${isSubmitDisabled ? disabledClasses : enabledClasses} ${defaultClasses}`}
        onClick={isSubmitDisabled ? undefined : () => resendInvitation(user!.id)}
        style={{ width: 200 }}
      >
        <SendMailIcon className="w-5 h-4 mr-3" />
        {resendingInvitation ? 'Sending...' : 'Resend Invitation'}
      </button>
    </Tooltip>
  );
}

function LoadingSkeleton () {
  return (
    <>
      <div className="sr-only">Loading</div>
      <div>
        <Skeleton className="w-12 h-12" variant="circle" />
      </div>
      <div className="col-start-2 col-span-full">
        <Skeleton className="w-1/4 h-16" variant="text" />
        <Skeleton className="w-1/2 h-12" variant="text" />

        <div className="flex h-16 pr-24 mt-6 gap-x-6">
          {[...Array(4)].map((_, index) => (
            <Skeleton key={index} className="w-1/2 h-full" variant="rect" />
          ))}
        </div>
      </div>
    </>
  );
}

type BillingHeaderProps = {
  account?: Account;
  rate?: number;
};
const BillingHeader = ({ account, rate }: BillingHeaderProps) => {
  if (!account?.paymentDate || !account?.billingInterval || !rate) {
    return null;
  }

  const newRate = (rate / 100).toFixed(2);
  let paymentDate = new Date(account.paymentDate);
  const dateStr = format(paymentDate, 'MM-dd-yyyy');
  return (
    <div className="flex text-sm bg-blue-200 rounded p-2.5 col-span-full gap-0">
      <WarningIcon className="w-5 h-5 mb-1 mr-2 text-blue-600" />
      Your new {account.billingInterval} rate will be ${newRate} (next payment of {dateStr}).
    </div>
  );
};
